import React, { useEffect, useMemo, useCallback, useRef } from "react";
import globals from "../../../globalVars";
import ListingPage from "../../../components/data/ListingPage";
import useStateModule from "../../../core/state/useStateModule";
import { useTranslation } from "react-i18next";
import useImmerReducer from "../../../hooks/_shared/useImmerReducer";
import usePageChange from "../../../hooks/_shared/usePageChange";
import cylinderListStateModule from "../../../core/state/company/cylinderListStateModule";
import equipmentListStateModule from "../../../core/state/company/equipmentListStateModule";
import FilterMenu from "../FilterMenu";
import { Select } from "../../../components/elements/form-elements";
import InputElement from "../../../components/elements/form-elements/InputElement";
import CylinderType from "../../../core/enums/CylinderType";

import { StyledFilter, StyledArrow, StyledBack, StyledClose } from "./StyledSvg";

import {
    ScreenWrapper,
    ScreenHeader,
    FlexForm,
    FilterClear,
    FilterWrapper,
    HR,
    ListTitle,
} from "./StyledElements";

const actionTypes = {
    SET_PAGING: "SET_PAGING",
    SET_SORT: "SET_SORT",
    RESET_SORT: "RESET_SORT",
    SET_FILTERS: "SET_FILTERS",
    RESET_FILTERS: "RESET_FILTERS",
};

const filterReducer = (state, action) => {
    switch (action.type) {
        case actionTypes.SET_PAGING:
            state.pageSize = action.payload.pageSize;
            state.page = action.payload.pageIndex + 1;
            break;
        case actionTypes.SET_SORT:
            state.orderColumn = action.payload[0].id;
            state.descending = action.payload[0].desc;
            break;
        case actionTypes.RESET_SORT:
            state.orderColumn = "extractionDateTime";
            state.descending = true;
            break;
        case actionTypes.SET_FILTERS:
            state.filter.name = action.payload.name;
            state.filter.macAddress = action.payload.macAddress;
            state.page = 1;
            break;
        case actionTypes.RESET_FILTERS:
            delete state.filter.name;
            delete state.filter.macAddress;
            state.page = 1;
            break;
        default:
            return state;
    }
};

const AnalyzerStockListing = (props) => {
    const { equipment, isLoading, error, loadEquipment, loadEquipmentById, details } =
        useStateModule(equipmentListStateModule);

    const [filterState, dispatch] = useImmerReducer(filterReducer, {
        filter: {
            name: "",
            macAddress: "",
        },
        page: 1,
        pageSize: globals.maxListingItemsPerPage,
        orderColumn: "name",
        descending: false,
    });

    const { t } = useTranslation("screens");

    const filterMenuControl = {};

    useEffect(() => {
        loadEquipment(filterState);
    }, [loadEquipment, filterState, props.reset]);

    const columns = useMemo(() => {
        const columns = [
            {
                Header: t("filters:clients.name"),
                accessor: "name",
            },
            {
                Header: "",
                accessor: "id",
                className: "right-arrow",
                Cell: ({ cell: { value } }) => <StyledArrow />,
                disableSortBy: true,
            },
        ];
        return columns;
    }, [filterState.filter, t]);

    const filterData = useRef({
        name: "",
        macAddress: "",
    });

    const tempFilterData = useRef({});

    const hasFilters = () => filterData.name || filterData.macAddress;

    const filterItems = () => {
        tempFilterData.current = {};
        return (
            <FlexForm autoComplete="off">
                <InputElement
                    labelText={t("filters:clients.name")}
                    id="name"
                    name="name"
                    wrapperVariant="flex-1"
                    type="text"
                    className="filters big"
                    defaultValue={filterData.current.name}
                    handleChange={(evt) => {
                        tempFilterData.current.name = evt.currentTarget.value;
                    }}
                />

                <InputElement
                    labelText={t("forms:analyzer.wizard.macAddress")}
                    id="macAddress"
                    name="macAddress"
                    wrapperVariant="flex-1"
                    type="text"
                    mask="**:**:**:**:**:**"
                    alwaysShowMask
                    className="filters big"
                    defaultValue={filterData.current.macAddress}
                    handleChange={(evt) => {
                        tempFilterData.current.macAddress = evt.currentTarget.value;
                    }}
                />
            </FlexForm>
        );
    };

    const applyFilters = () => {
        Object.assign(filterData.current, tempFilterData.current);
        dispatch({
            type: actionTypes.SET_FILTERS,
            payload: filterData.current,
        });
    };

    const clearFilters = () => {
        filterData.current = {
            name: "",
            macAddress: "",
        };
        dispatch({
            type: actionTypes.RESET_FILTERS,
        });
    };

    const clearFilter = (name) => {
        switch (name) {
            case "name":
                filterData.current.name = "";
                break;
            case "macAddress":
                filterData.current.macAddress = "";
                break;
            default:
                break;
        }
        dispatch({
            type: actionTypes.SET_FILTERS,
            payload: filterData.current,
        });
    };

    const showFilter = (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        filterMenuControl.setState({
            isOpen: true,
            hasFilters: hasFilters(),
            filterItems: filterItems(),
            applyFn: applyFilters,
            clearFn: clearFilters,
            text: hasFilters()
                ? t("screens:stockListing.filters.clearFilters")
                : t("forms:analyzer.filterAnalyzers"),
        });
    };

    const onSelect = useCallback((row) => {
        props.onSelect && props.onSelect(row.original);
    }, []);

    const pageChange = usePageChange(dispatch, filterState);

    const sortChange = (sortBy) => {
        sortBy.length > 0
            ? dispatch({ type: actionTypes.SET_SORT, payload: sortBy })
            : dispatch({ type: actionTypes.RESET_SORT });
    };

    const currentSortBy = useMemo(() => {
        return [{ id: filterState.orderColumn, desc: filterState.descending }];
    }, [filterState.orderColumn, filterState.descending]);

    const curFilters = filterData.current;
    return (
        <ScreenWrapper>
            <FilterMenu control={filterMenuControl} />
            <ScreenHeader>
                <StyledBack className="disabled" />
                <FilterWrapper>
                    {curFilters.name && (
                        <FilterClear onClick={() => clearFilter("name")}>
                            {t("forms:analyzer.wizard.name")}
                            <StyledClose />
                        </FilterClear>
                    )}

                    {curFilters.macAddress && (
                        <FilterClear onClick={() => clearFilter("macAddress")}>
                            {t("forms:analyzer.wizard.macAddress")}
                            <StyledClose />
                        </FilterClear>
                    )}

                    <StyledFilter onClick={showFilter} />
                </FilterWrapper>
            </ScreenHeader>
            <HR />
            <ListTitle>{t("forms:analyzer.analyzers")}</ListTitle>
            <ListingPage
                noHeader
                pwa
                columns={columns}
                tableState={{
                    data: equipment.data,
                    loading: isLoading,
                    error: error,
                }}
                paging={{
                    enablePaging: true,
                    pageCount: equipment.totalPages,
                    pageSize: equipment.pageSize,
                    onPageChange: pageChange,
                    currentPageIndex: equipment.currentPage - 1,
                }}
                sorting={{
                    enableSorting: true,
                    onSortByChange: sortChange,
                    currentSortBy: currentSortBy,
                }}
                onSelect={onSelect}
                noDataText={t("screens:stockListing.notification")}
            />
        </ScreenWrapper>
    );
};

export default AnalyzerStockListing;
